import axios from "axios";
import { useState, useContext } from "react";
import { AuthContext } from "../../context/AuthContext";
import {
    Card,
    Input,
    Button,
    Form,
    DatePicker,
    message,
    Descriptions,
    Divider,
    Typography,
    Modal,
    Table,
    Tag,
} from "antd";
import { SearchOutlined, SaveOutlined, EyeOutlined } from "@ant-design/icons";
import dayjs from "dayjs";

const { Title, Link } = Typography;

const STATUS_COLORS = {
    "Pending Approval": "orange",
    Available: "green",
    Rejected: "red",
    Availed: "blue",
    Expired: "default",
    PartiallyUsed: "gold",
    Cancelled: "default",
};

export default function AddCompoff() {
    const [empCode, setEmpCode] = useState("");
    const [employee, setEmployee] = useState(null);
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [form] = Form.useForm();
    const { authData, backUrl } = useContext(AuthContext);

    // ✅ View Compoffs modal state
    const [viewModalOpen, setViewModalOpen] = useState(false);
    const [compoffList, setCompoffList] = useState([]);
    const [fetchingList, setFetchingList] = useState(false);

    // ✅ Fetch employee by code
    const fetchEmployee = async () => {
        if (!empCode.trim()) {
            message.warning("Please enter an employee code.");
            return;
        }
        setLoading(true);
        try {
            const res = await axios.get(
                `${backUrl}/api/emp/employees?emp_code=${empCode}`,
                {
                    headers: { Authorization: `Bearer ${authData?.access}` },
                }
            );
            const result = res.data.results[0];
            if (!result) {
                message.error("No employee found with that code.");
                setEmployee(null);
            } else {
                setEmployee(result);
                message.success("Employee found!");
            }
        } catch (err) {
            message.error("Failed to fetch employee. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    // ✅ Fetch comp-offs created by the logged-in user
    const fetchMyCompoffs = async () => {
        if (!authData?.employee?.id) {
            message.warning("Unable to determine logged-in employee.");
            return;
        }
        setFetchingList(true);
        try {
            const res = await axios.get(`${backUrl}/api/hr/compoffs/`, {
                params: { created_by: authData.employee.id },
                headers: { Authorization: `Bearer ${authData?.access}` },
            });
            setCompoffList(res.data?.results || []);
        } catch (err) {
            message.error("Failed to fetch comp-off records.");
            setCompoffList([]);
        } finally {
            setFetchingList(false);
        }
    };

    const handleViewCompoffs = () => {
        setViewModalOpen(true);
        fetchMyCompoffs();
    };

    // ✅ Submit compoff
    const handleSubmit = async (values) => {
        if (!employee) {
            message.warning("Please search and select an employee first.");
            return;
        }
        setSubmitting(true);
        try {
            const payload = {
                employee: employee?.id,
                work_date: dayjs(values.work_date).format("YYYY-MM-DD"),
                reason: values.reason,
                created_by: authData?.employee?.id
            };

            await axios.post(
                `${backUrl}/api/hr/compoffs/`,
                payload,
                {
                    headers: {
                        Authorization: `Bearer ${authData?.access}`,
                        "Content-Type": "application/json",
                    },
                }
            );

            message.success("Comp-off added successfully!");
            form.resetFields();
            setEmployee(null);
            setEmpCode("");
        } catch (err) {
            let errMsg = "Failed to add comp-off.";

            if (err.response) {
                errMsg =
                    err.response.data?.message ||
                    err.response.data?.detail ||
                    JSON.stringify(err.response.data);
            } else if (err.request) {
                errMsg = "No response from server. Please try again.";
            } else {
                errMsg = err.message;
            }

            message.error(errMsg);
        } finally {
            setSubmitting(false);
        }
    };

    const columns = [
        {
            title: "Employee Code",
            dataIndex: ["employee", "emp_code"],
            key: "reason",
            ellipsis: true,
        },
        {
            title: "Employee Name",
            dataIndex: ["employee", "full_name"],
            key: "reason",
            ellipsis: true,
        },
        {
            title: "Organisation",
            dataIndex: ["employee", "organisation_name"],
            key: "reason",
            ellipsis: true,
        },
        {
            title: "Department",
            dataIndex: ["employee", "department_name"],
            key: "reason",
            ellipsis: true,
        },
        {
            title: "Designation",
            dataIndex: ["employee", "designation_name"],
            key: "reason",
            ellipsis: true,
        },
        {
            title: "Work Date",
            dataIndex: "work_date",
            key: "work_date",
            render: (val) => (val ? dayjs(val).format("DD/MM/YYYY") : "-"),
        },
        {
            title: "Reason",
            dataIndex: "reason",
            key: "reason",
            ellipsis: true,
        },
        {
            title: "Status",
            dataIndex: "status",
            key: "status",
            render: (status) => (
                <Tag color={STATUS_COLORS[status] || "default"}>{status || "-"}</Tag>
            ),
        },
        {
            title: "Days Available",
            dataIndex: "days_available",
            key: "days_available",
        },
        {
            title: "Expiry Date",
            dataIndex: "expiry_date",
            key: "expiry_date",
            render: (val) => (val ? dayjs(val).format("DD/MM/YYYY") : "-"),
        },
        {
            title: "Created At",
            dataIndex: "created_at",
            key: "created_at",
            render: (val) => (val ? dayjs(val).format("DD/MM/YYYY hh:mm A") : "-"),
        },
    ];

    return (
        <div style={{ padding: "24px" }}>
            <Card
                style={{
                    maxWidth: 700,
                    margin: "0 auto",
                    borderRadius: 12,
                    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                }}
            >
                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        position: "relative",
                    }}
                >
                    <Title level={4} style={{ textAlign: "center", color: "#0e497b", margin: 0 }}>
                        Add Comp-Off
                    </Title>
                    <Link
                        onClick={handleViewCompoffs}
                        style={{
                            position: "absolute",
                            right: 0,
                            display: "flex",
                            alignItems: "center",
                            gap: 4,
                        }}
                    >
                        <EyeOutlined /> View Compoffs
                    </Link>
                </div>

                <Divider />

                <Form.Item label="Search Employee" labelCol={{ span: 24 }}>
                    <Input.Search
                        placeholder="Enter Employee Code"
                        value={empCode}
                        onChange={(e) => setEmpCode(e.target.value)}
                        onSearch={fetchEmployee}
                        enterButton={
                            <Button
                                loading={loading}
                                icon={<SearchOutlined />}
                                style={{ backgroundColor: "#0e497b", color: "white" }}
                            >
                                Search
                            </Button>
                        }
                        onPressEnter={fetchEmployee}
                    />
                </Form.Item>

                {/* ✅ Employee Details */}
                {employee && (
                    <>
                        <Descriptions
                            bordered
                            size="small"
                            column={2}
                            style={{ marginBottom: 20 }}
                        >
                            <Descriptions.Item label="Employee Code">
                                {employee.emp_code || "-"}
                            </Descriptions.Item>
                            <Descriptions.Item label="Name">
                                {employee?.user?.first_name + " " + employee?.user?.last_name || "-"}
                            </Descriptions.Item>
                            <Descriptions.Item label="Department">
                                {employee.department_name || "-"}
                            </Descriptions.Item>
                            <Descriptions.Item label="Designation">
                                {employee.designation_name || "-"}
                            </Descriptions.Item>
                        </Descriptions>

                        <Divider />

                        {/* ✅ Compoff Form */}
                        <Form
                            form={form}
                            layout="vertical"
                            onFinish={handleSubmit}
                        >
                            <Form.Item
                                label="Work Date"
                                name="work_date"
                                rules={[{ required: true, message: "Please select the work date." }]}
                            >
                                <DatePicker
                                    style={{ width: "100%" }}
                                    format="DD/MM/YYYY"
                                    disabledDate={(current) =>
                                        current && current > dayjs().endOf("day")
                                    }
                                />
                            </Form.Item>

                            <Form.Item
                                label="Reason"
                                name="reason"
                                rules={[{ required: true, message: "Please enter a reason." }]}
                            >
                                <Input.TextArea
                                    rows={3}
                                    placeholder="Enter reason for comp-off..."
                                />
                            </Form.Item>

                            <Form.Item>
                                <Button
                                    type="primary"
                                    htmlType="submit"
                                    loading={submitting}
                                    icon={<SaveOutlined />}
                                    block
                                    style={{
                                        backgroundColor: "#0e497b",
                                        borderColor: "#0e497b",
                                        height: 40,
                                        borderRadius: 6,
                                    }}
                                >
                                    Submit Comp-Off
                                </Button>
                            </Form.Item>
                        </Form>
                    </>
                )}
            </Card>

            {/* ✅ View Compoffs Modal */}
            <Modal
                title="My Comp-Off Requests"
                open={viewModalOpen}
                onCancel={() => setViewModalOpen(false)}
                footer={null}
                width={800}
            >
                <Table
                    columns={columns}
                    dataSource={compoffList}
                    rowKey={(record) => record.id}
                    loading={fetchingList}
                    pagination={{ pageSize: 10 }}
                    scroll={{ x: true }}
                />
            </Modal>
        </div>
    );
}